/*
 * PotterMe booking wizard.
 * A dependency-free port of components/booking/booking-wizard.tsx — same markup,
 * same class names (with the `bw-` CSS-module prefix), same copy, same fetch
 * contract, same focus and aria-live behaviour.
 *
 * What a theme cannot do happens in the Apps Script web app in
 * integrations/google-calendar.gs: it reads open times from the studio's
 * Google Calendars and saves table bookings, which need no payment. A deposit
 * is Shopify's: the wizard adds the deposit product to the cart with the date,
 * time and painter count as line item properties and hands the customer to
 * Shopify checkout; the paid order reaches the calendar through Shopify's
 * order webhook.
 */
(function () {
  'use strict';

  var STEP_KEYS = ['experience', 'date_time', 'your_details', 'review'];

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function fill(template, values) {
    return String(template == null ? '' : template).replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
    });
  }

  // Shopify's `t` filter HTML-escapes translations ("Date &amp; time") and the
  // strings arrive here through JSON, so they are decoded once before use.
  var entityDecoder = document.createElement('textarea');
  function decodeEntities(value) {
    entityDecoder.innerHTML = String(value);
    return entityDecoder.value;
  }

  class PotterMeBookingWizard extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.offers = this.readJson('[data-booking-offers]') || [];
      this.config = this.readJson('[data-booking-config]') || {};
      this.t = this.config.t || {};
      this.timezone = this.config.timezone || 'America/New_York';
      this.iconRoot = this.querySelector('[data-booking-icons]');

      if (!this.offers.length) return;

      this.draft = {
        offerId: '',
        partySize: 1,
        childGuests: 0,
        date: '',
        startsAt: '',
        agePolicyAcknowledged: false,
        customer: { firstName: '', lastName: '', email: '', phone: '' },
        notes: ''
      };
      this.step = 0;
      this.slots = [];
      this.availabilityState = 'idle';
      this.error = '';
      this.submitting = false;
      this.availabilityController = null;

      this.renderShell();
      this.render();
    }

    readJson(selector) {
      var node = this.querySelector(selector);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (reason) {
        return null;
      }
    }

    icon(name) {
      if (!this.iconRoot) return '';
      var template = this.iconRoot.querySelector('[data-icon="' + name + '"]');
      return template ? template.innerHTML.trim() : '';
    }

    label(key) {
      return this.t[key] == null ? '' : decodeEntities(this.t[key]);
    }

    /* ---------------------------------------------------------------- format */

    formatMoney(cents) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      }).format(cents / 100);
    }

    formatSlot(startsAt) {
      return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: this.timezone
      }).format(new Date(startsAt));
    }

    formatDateTime(startsAt) {
      if (!startsAt) return '';
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: this.timezone,
        timeZoneName: 'short'
      }).format(new Date(startsAt));
    }

    todayInStudioTime() {
      return new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: this.timezone
      }).format(new Date());
    }

    /* ----------------------------------------------------------------- state */

    get offer() {
      var self = this;
      return this.offers.filter(function (item) {
        return item.id === self.draft.offerId;
      })[0];
    }

    /** What Shopify Payments will charge at checkout, in cents. */
    get reservationTotal() {
      var offer = this.offer;
      if (!offer || offer.quantityMode === 'none') return 0;
      if (offer.quantityMode === 'fixed') return offer.unitPriceCents || 0;
      var children = this.childGuests;
      var adults = Math.max(0, this.draft.partySize - children);
      var childPrice = offer.childUnitPriceCents || offer.unitPriceCents || 0;
      return adults * (offer.unitPriceCents || 0) + children * childPrice;
    }

    /** Child count, clamped to the party size and to offers that have a child rate. */
    get childGuests() {
      var offer = this.offer;
      if (!offer || !offer.childVariantId || offer.quantityMode !== 'per-guest') return 0;
      return Math.min(Math.max(0, Number(this.draft.childGuests) || 0), this.draft.partySize);
    }

    setError(message) {
      this.error = message;
      this.renderError();
      if (message && this.errorNode) this.errorNode.focus();
    }

    /* ---------------------------------------------------------------- render */

    renderShell() {
      var progressWrap = document.createElement('div');
      progressWrap.className = 'bw-progressWrap';
      progressWrap.innerHTML = '<ol class="bw-progress" aria-label="' + escapeHtml(this.label('progress_label')) + '"></ol>';
      this.progressList = progressWrap.querySelector('.bw-progress');

      var form = document.createElement('form');
      form.className = 'bw-formPaper';
      form.noValidate = false;
      form.innerHTML =
        '<span class="bw-tape" aria-hidden="true"></span>' +
        '<p class="bw-stepAnnouncement" aria-live="polite"></p>' +
        '<div data-error-slot></div>' +
        '<div data-step-slot></div>';
      this.form = form;
      this.announcement = form.querySelector('.bw-stepAnnouncement');
      this.errorSlot = form.querySelector('[data-error-slot]');
      this.stepSlot = form.querySelector('[data-step-slot]');

      var summary = document.createElement('aside');
      summary.className = 'bw-summary';
      summary.setAttribute('aria-label', this.label('summary_label'));
      summary.hidden = true;
      this.summary = summary;

      var noscript = this.querySelector('noscript');
      if (noscript) noscript.remove();

      this.appendChild(progressWrap);
      this.appendChild(form);
      this.appendChild(summary);

      var self = this;
      form.addEventListener('submit', function (event) {
        self.submitBooking(event);
      });
      form.addEventListener('click', function (event) {
        self.handleClick(event);
      });
      form.addEventListener('input', function (event) {
        self.handleInput(event);
      });
      form.addEventListener('change', function (event) {
        self.handleChange(event);
      });
    }

    render() {
      this.renderProgress();
      this.renderAnnouncement();
      this.renderError();
      this.renderStep();
      this.renderSummary();
    }

    renderProgress() {
      var self = this;
      this.progressList.innerHTML = STEP_KEYS.map(function (key, index) {
        var marker = index < self.step ? self.icon('check') : String(index + 1);
        return (
          '<li' + (index === self.step ? ' aria-current="step"' : '') +
          ' class="' + (index <= self.step ? 'bw-progressDone' : '') + '">' +
          '<span>' + marker + '</span>' +
          escapeHtml(self.label('step_' + key)) +
          '</li>'
        );
      }).join('');
    }

    renderAnnouncement() {
      this.announcement.textContent = fill(this.label('step_announcement'), {
        step: this.step + 1,
        total: STEP_KEYS.length,
        label: this.label('step_' + STEP_KEYS[this.step])
      });
    }

    renderError() {
      if (!this.error) {
        this.errorSlot.innerHTML = '';
        this.errorNode = null;
        return;
      }
      this.errorSlot.innerHTML =
        '<div class="bw-error" role="alert" tabindex="-1">' + escapeHtml(this.error) + '</div>';
      this.errorNode = this.errorSlot.firstElementChild;
    }

    renderStep() {
      var html = '';
      if (this.step === 0) html = this.stepExperienceHtml();
      else if (this.step === 1) html = this.stepScheduleHtml();
      else if (this.step === 2) html = this.stepDetailsHtml();
      else if (this.step === 3) html = this.stepReviewHtml();
      this.stepSlot.innerHTML = html;
      if (this.step === 1) this.renderSlots();
    }

    /* ------------------------------------------------------------- step zero */

    offerCardHtml(offer, primary) {
      var classes = ['bw-offerCard', 'bw-accent' + offer.accent];
      if (primary) classes.push('bw-primaryOfferCard');
      var heading = primary ? 'h3' : 'h4';
      var hours = offer.durationMinutes / 60;

      var packageLine = '';
      if (offer.perPainterPackageCents) {
        packageLine =
          '<li>' + this.icon('party-popper') + ' ' +
          escapeHtml(fill(this.label('offer_package'), { money: this.formatMoney(offer.perPainterPackageCents) })) +
          '</li>';
      }

      var priceKey = offer.quantityMode === 'fixed' ? 'offer_deposit' : 'offer_per_painter';
      var priceLine = offer.quantityMode === 'none'
        ? '<strong>' + escapeHtml(this.label('offer_pay_in_studio')) + '</strong>'
        : '<strong>' + escapeHtml(fill(this.label(priceKey), { money: this.formatMoney(offer.unitPriceCents || 0) })) +
          '</strong> ' + escapeHtml(this.label('offer_due_today'));

      return (
        '<article class="' + classes.join(' ') + '">' +
        (primary ? '<span class="bw-primaryBadge">' + escapeHtml(this.label('offer_badge')) + '</span>' : '') +
        this.icon('clay-' + offer.icon) +
        '<p class="bw-offerEyebrow">' + escapeHtml(offer.eyebrow) + '</p>' +
        '<' + heading + '>' + escapeHtml(offer.name) + '</' + heading + '>' +
        '<p class="bw-offerDescription">' + escapeHtml(offer.shortDescription) + '</p>' +
        '<ul>' +
        '<li>' + this.icon('clock-3') + ' ' + escapeHtml(fill(this.label('offer_duration'), { hours: hours })) + '</li>' +
        '<li>' + this.icon('users') + ' ' + escapeHtml(fill(this.label('offer_guests'), { min: offer.minimumPartySize, max: offer.maximumPartySize })) + '</li>' +
        packageLine +
        '</ul>' +
        (offer.durationNote ? '<p class="bw-durationNote"><small>' + escapeHtml(offer.durationNote) + '</small></p>' : '') +
        '<p class="bw-priceLine">' + priceLine + '</p>' +
        '<button type="button" class="bw-chooseButton" data-choose="' + escapeHtml(offer.id) + '">' +
        escapeHtml(fill(this.label('offer_choose'), { name: offer.name })) + ' <span aria-hidden="true">→</span>' +
        '</button>' +
        '</article>'
      );
    }

    stepExperienceHtml() {
      var self = this;
      var primaryOffer = this.offers.filter(function (item) { return item.primary; })[0];
      var partyOffers = this.offers.filter(function (item) { return item !== primaryOffer; });

      var partyBlock = '';
      if (partyOffers.length > 0) {
        partyBlock =
          '<section class="bw-partyOptions" aria-labelledby="party-options-title">' +
          '<div class="bw-partyOptionsHeader">' +
          '<p class="bw-kicker">' + escapeHtml(this.label('party_kicker')) + '</p>' +
          '<h3 id="party-options-title">' + escapeHtml(this.label('party_title')) + '</h3>' +
          '<p>' + escapeHtml(this.label('party_copy')) + '</p>' +
          '</div>' +
          '<div class="bw-partyOfferGrid">' +
          partyOffers.map(function (offer) { return self.offerCardHtml(offer, false); }).join('') +
          '</div>' +
          '</section>';
      }

      return (
        '<div>' +
        '<p class="bw-kicker">' + escapeHtml(this.label('experience_kicker')) + '</p>' +
        '<h2 id="booking-wizard-title">' + escapeHtml(this.label('experience_title')) + '</h2>' +
        '<p class="bw-lede">' + escapeHtml(this.label('experience_lede')) + '</p>' +
        (primaryOffer ? this.offerCardHtml(primaryOffer, true) : '') +
        partyBlock +
        '</div>'
      );
    }

    /* -------------------------------------------------------------- step one */

    stepScheduleHtml() {
      var offer = this.offer;
      if (!offer) return '';
      var childField = '';
      if (offer.childVariantId && offer.quantityMode === 'per-guest') {
        childField =
          '<label>' +
          '<span>' + escapeHtml(offer.childLabel || this.label('child_guests_label')) + '</span>' +
          '<input type="number" data-field="childGuests" min="0" max="' + offer.maximumPartySize + '" value="' + this.childGuests + '">' +
          '<small>' + escapeHtml(fill(this.label('child_guests_hint'), { money: this.formatMoney(offer.childUnitPriceCents || 0) })) + '</small>' +
          '</label>';
      }
      return (
        '<div>' +
        '<p class="bw-kicker">' + escapeHtml(this.label('schedule_kicker')) + '</p>' +
        '<h2 id="booking-wizard-title">' + escapeHtml(this.label('schedule_title')) + '</h2>' +
        '<p class="bw-lede">' + escapeHtml(this.label('schedule_lede')) + '</p>' +
        '<fieldset class="bw-fieldset">' +
        '<legend class="bw-visuallyHidden">' + escapeHtml(this.label('schedule_legend')) + '</legend>' +
        '<div class="bw-fieldGrid">' +
        '<label>' +
        '<span>' + escapeHtml(this.label('guests_label')) + '</span>' +
        '<input type="number" data-field="partySize" min="' + offer.minimumPartySize + '" max="' + offer.maximumPartySize + '" value="' + this.draft.partySize + '" required>' +
        '<small>' + escapeHtml(fill(this.label('guests_hint'), { min: offer.minimumPartySize, max: offer.maximumPartySize })) + '</small>' +
        '</label>' +
        childField +
        '<label>' +
        '<span>' + escapeHtml(this.label('date_label')) + '</span>' +
        '<input type="date" data-field="date" min="' + this.todayInStudioTime() + '" value="' + escapeHtml(this.draft.date) + '" required>' +
        '<small>' + escapeHtml(this.config.closedNote || '') + '</small>' +
        '</label>' +
        '</div>' +
        '</fieldset>' +
        '<fieldset class="bw-slotFieldset" data-slot-fieldset>' +
        '<legend>' + escapeHtml(this.label('slots_legend')) + '</legend>' +
        '<p class="bw-visuallyHidden" role="status" aria-live="polite" aria-atomic="true" data-availability-announcement></p>' +
        '<div data-slot-messages></div>' +
        '<div class="bw-slotGrid" data-slot-grid></div>' +
        this.callNote() +
        '</fieldset>' +
        '<div class="bw-actions">' +
        '<button type="button" class="bw-backButton" data-goto="0">' + this.icon('chevron-left') + ' ' + escapeHtml(this.label('back')) + '</button>' +
        '<button type="button" class="button button-ink" data-continue="details">' + escapeHtml(this.label('continue_details')) + ' <span aria-hidden="true">→</span></button>' +
        '</div>' +
        '</div>'
      );
    }

    /** The nudge under the times: "Don't see the time you want? Call the studio." */
    callNote() {
      var note = this.config.callNote;
      if (!note) return '';
      var phone = this.config.studioPhone;
      var display = this.config.studioPhoneDisplay || phone;
      return (
        '<p class="bw-slotCallNote">' + escapeHtml(note) +
        (phone ? ' <a href="tel:' + escapeHtml(phone) + '">' + escapeHtml(display) + '</a>' : '') +
        '</p>'
      );
    }

    renderSlots() {
      var fieldset = this.stepSlot.querySelector('[data-slot-fieldset]');
      if (!fieldset) return;
      var messages = fieldset.querySelector('[data-slot-messages]');
      var grid = fieldset.querySelector('[data-slot-grid]');
      var announcement = fieldset.querySelector('[data-availability-announcement]');
      var self = this;

      fieldset.setAttribute('aria-busy', this.availabilityState === 'loading' ? 'true' : 'false');

      var notes = '';
      if (!this.draft.date) {
        notes += '<p class="bw-emptyState">' + escapeHtml(this.label('slots_pick_date')) + '</p>';
      }
      if (this.availabilityState === 'loading') {
        notes += '<p class="bw-emptyState">' + escapeHtml(this.label('slots_loading')) + '</p>';
      }
      if (this.availabilityState === 'error') {
        notes += '<p class="bw-emptyState">' + escapeHtml(this.label('slots_error')) + '</p>';
      }
      if (this.availabilityState === 'ready' && this.slots.length === 0) {
        notes += '<p class="bw-emptyState">' + escapeHtml(this.label('slots_none')) + '</p>';
      }
      messages.innerHTML = notes;

      grid.innerHTML = this.slots.map(function (slot) {
        var selected = self.draft.startsAt === slot.startsAt;
        return (
          '<button type="button" aria-pressed="' + (selected ? 'true' : 'false') + '"' +
          ' class="' + (selected ? 'bw-slotSelected' : 'bw-slot') + '"' +
          ' data-slot="' + escapeHtml(slot.startsAt) + '">' +
          self.icon('clock-3') +
          '<strong>' + escapeHtml(self.formatSlot(slot.startsAt)) + '</strong>' +
          '<small>' + escapeHtml(fill(self.label('slot_seats'), { count: slot.remaining })) + '</small>' +
          '</button>'
        );
      }).join('');

      announcement.textContent = this.availabilityAnnouncement();
    }

    availabilityAnnouncement() {
      if (this.availabilityState === 'loading') return this.label('announce_loading');
      if (this.availabilityState === 'error') return this.label('announce_error');
      if (this.availabilityState === 'ready') {
        if (this.slots.length > 0) {
          return fill(this.label('announce_found'), {
            count: this.slots.length,
            noun: this.slots.length === 1 ? this.label('time_singular') : this.label('time_plural')
          });
        }
        return this.label('announce_none');
      }
      return '';
    }

    /** Dates the studio has closed by hand, e.g. a day that is already full. */
    isClosed(date) {
      return String(this.config.blockedDates || '').split(',').some(function (day) {
        return day.trim() === date;
      });
    }

    loadAvailability() {
      if (!this.draft.date || !this.draft.offerId || this.step !== 1) return;
      if (this.availabilityController) this.availabilityController.abort();
      if (this.isClosed(this.draft.date)) {
        this.slots = [];
        this.availabilityState = 'ready';
        this.renderSlots();
        return;
      }
      var controller = new AbortController();
      this.availabilityController = controller;

      var base = this.config.availabilityUrl;
      if (!base) {
        this.slots = [];
        this.availabilityState = 'error';
        this.renderSlots();
        return;
      }
      var query = new URLSearchParams({
        action: 'availability',
        date: this.draft.date,
        offerId: this.draft.offerId,
        partySize: String(this.draft.partySize)
      });
      var separator = base.indexOf('?') === -1 ? '?' : '&';
      var self = this;
      fetch(base + separator + query.toString(), { signal: controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('Availability could not be loaded.');
          return response.json();
        })
        .then(function (data) {
          if (data && data.error) throw new Error(data.error);
          self.slots = data.slots || [];
          self.availabilityState = 'ready';
          self.renderSlots();
        })
        .catch(function (reason) {
          if (reason && reason.name === 'AbortError') return;
          self.slots = [];
          self.availabilityState = 'error';
          self.renderSlots();
        });
    }

    /** Line item properties the studio (and the calendar webhook) sees on the order. */
    cartProperties(offer) {
      var properties = {
        'Experience': offer.name,
        'Date & time': this.formatDateTime(this.draft.startsAt),
        'Painters': String(this.draft.partySize),
        'Phone': this.draft.customer.phone,
        '_offerId': offer.id,
        '_startsAt': this.draft.startsAt,
        '_guests': String(this.draft.partySize),
        '_childGuests': String(this.childGuests)
      };
      if (this.childGuests > 0) properties[offer.childLabel || 'Age 12 & under'] = String(this.childGuests);
      if (this.draft.notes) properties['Studio note'] = this.draft.notes;
      return properties;
    }

    /** One line for the full-rate painters, plus one for the reduced rate. */
    cartItems(offer) {
      var properties = this.cartProperties(offer);
      var items = [];
      if (offer.quantityMode === 'fixed') {
        items.push({ id: Number(offer.variantId), quantity: 1, properties: properties });
        return items;
      }
      var children = this.childGuests;
      var adults = Math.max(0, this.draft.partySize - children);
      if (adults > 0) items.push({ id: Number(offer.variantId), quantity: adults, properties: properties });
      if (children > 0 && offer.childVariantId) {
        items.push({ id: Number(offer.childVariantId), quantity: children, properties: properties });
      }
      return items;
    }

    /* -------------------------------------------------------------- step two */

    stepDetailsHtml() {
      var self = this;
      var fields = [
        { key: 'firstName', type: 'text', autocomplete: 'given-name' },
        { key: 'lastName', type: 'text', autocomplete: 'family-name' },
        { key: 'email', type: 'email', autocomplete: 'email' },
        { key: 'phone', type: 'tel', autocomplete: 'tel' }
      ];
      return (
        '<div>' +
        '<p class="bw-kicker">' + escapeHtml(this.label('details_kicker')) + '</p>' +
        '<h2 id="booking-wizard-title">' + escapeHtml(this.label('details_title')) + '</h2>' +
        '<p class="bw-lede">' + escapeHtml(this.label('details_lede')) + '</p>' +
        '<fieldset class="bw-fieldset">' +
        '<legend class="bw-visuallyHidden">' + escapeHtml(this.label('details_legend')) + '</legend>' +
        '<div class="bw-fieldGrid">' +
        fields.map(function (field) {
          return (
            '<label>' +
            '<span>' + escapeHtml(self.label('field_' + field.key)) + '</span>' +
            '<input type="' + field.type + '" autocomplete="' + field.autocomplete + '"' +
            ' data-customer="' + field.key + '" value="' + escapeHtml(self.draft.customer[field.key]) + '" required>' +
            '</label>'
          );
        }).join('') +
        '</div>' +
        '<label class="bw-fullField">' +
        '<span>' + escapeHtml(this.label('notes_label')) + ' <em>' + escapeHtml(this.label('optional')) + '</em></span>' +
        '<textarea rows="4" maxlength="600" data-field="notes" placeholder="' + escapeHtml(this.label('notes_placeholder')) + '">' + escapeHtml(this.draft.notes) + '</textarea>' +
        '</label>' +
        '</fieldset>' +
        '<div class="bw-actions">' +
        '<button type="button" class="bw-backButton" data-goto="1">' + this.icon('chevron-left') + ' ' + escapeHtml(this.label('back')) + '</button>' +
        '<button type="button" class="button button-ink" data-continue="review">' + escapeHtml(this.label('continue_review')) + ' <span aria-hidden="true">→</span></button>' +
        '</div>' +
        '</div>'
      );
    }

    /* ------------------------------------------------------------ step three */

    stepReviewHtml() {
      var offer = this.offer;
      if (!offer) return '';
      var draft = this.draft;
      var total = this.reservationTotal;

      var checkout = offer.quantityMode !== 'none';
      var lede = offer.confirmationMode === 'deposit-required'
        ? this.label('review_lede_deposit')
        : this.label(checkout ? 'review_lede_checkout' : 'review_lede_instant');

      var rows =
        '<div><dt>' + escapeHtml(this.label('review_experience')) + '</dt><dd>' + escapeHtml(offer.name) + '</dd></div>' +
        '<div><dt>' + escapeHtml(this.label('review_datetime')) + '</dt><dd>' + escapeHtml(this.formatDateTime(draft.startsAt)) + '</dd></div>' +
        '<div><dt>' + escapeHtml(this.label('review_crew')) + '</dt><dd>' +
        escapeHtml(fill(this.label('guest_count'), {
          count: draft.partySize,
          plural: draft.partySize === 1 ? '' : 's'
        })) + '</dd></div>' +
        '<div><dt>' + escapeHtml(this.label('review_name')) + '</dt><dd>' +
        escapeHtml(draft.customer.firstName) + ' ' + escapeHtml(draft.customer.lastName) + '</dd></div>' +
        '<div><dt>' + escapeHtml(this.label('review_contact')) + '</dt><dd>' +
        escapeHtml(draft.customer.email) + '<br>' + escapeHtml(draft.customer.phone) + '</dd></div>' +
        (this.childGuests > 0
          ? '<div><dt>' + escapeHtml(offer.childLabel || this.label('child_guests_label')) + '</dt><dd>' +
            escapeHtml(String(this.childGuests)) + '</dd></div>'
          : '') +
        (draft.notes
          ? '<div><dt>' + escapeHtml(this.label('review_note')) + '</dt><dd>' + escapeHtml(draft.notes) + '</dd></div>'
          : '');

      var depositStrong = total === 0
        ? this.label('review_no_payment')
        : fill(this.label('review_charged_now'), { money: this.formatMoney(total) });

      var ageConsent = '';
      if (offer.maximumParticipantAge) {
        ageConsent =
          '<label class="bw-consent">' +
          '<input type="checkbox" data-consent="age"' + (draft.agePolicyAcknowledged ? ' checked' : '') + ' required>' +
          '<span>' + escapeHtml(fill(this.label('consent_age'), { age: offer.maximumParticipantAge })) + '</span>' +
          '</label>';
      }

      var submitLabel = this.submitting
        ? this.label(checkout ? 'submit_redirecting' : 'submit_saving')
        : offer.confirmationMode === 'deposit-required'
          ? this.label('submit_request')
          : this.label(checkout ? 'submit_checkout' : 'submit_confirm');

      return (
        '<div>' +
        '<p class="bw-kicker">' + escapeHtml(this.label('review_kicker')) + '</p>' +
        '<h2 id="booking-wizard-title">' + escapeHtml(this.label('review_title')) + '</h2>' +
        '<p class="bw-lede">' + escapeHtml(lede) + '</p>' +
        '<dl class="bw-reviewList">' + rows + '</dl>' +
        '<div class="bw-depositNote">' +
        this.icon('party-popper') +
        '<div><strong>' + escapeHtml(depositStrong) + '</strong><p>' + escapeHtml(offer.priceNote) + '</p></div>' +
        '</div>' +
        ageConsent +
        '<label class="bw-consent">' +
        '<input type="checkbox" required>' +
        '<span>' + escapeHtml(this.label('consent_policies_before')) + ' ' +
        '<a href="' + escapeHtml(offer.policyUrl) + '" target="_blank" rel="noreferrer">' +
        escapeHtml(this.label('consent_policies_link')) + '</a>' +
        escapeHtml(this.label('consent_policies_after')) + '</span>' +
        '</label>' +
        '<div class="bw-actions">' +
        '<button type="button" class="bw-backButton" data-goto="2">' + this.icon('chevron-left') + ' ' + escapeHtml(this.label('back')) + '</button>' +
        '<button type="submit" class="button button-ink"' + (this.submitting ? ' disabled' : '') + '>' +
        escapeHtml(submitLabel) + ' ' + this.icon('calendar-days') +
        '</button>' +
        '</div>' +
        '</div>'
      );
    }

    /* -------------------------------------------------------------- summary */

    renderSummary() {
      var offer = this.offer;
      if (!offer) {
        this.summary.hidden = true;
        this.summary.innerHTML = '';
        return;
      }
      var total = this.reservationTotal;
      this.summary.hidden = false;
      this.summary.innerHTML =
        '<span class="bw-summaryTape" aria-hidden="true"></span>' +
        '<p class="bw-summaryScribble">' + escapeHtml(this.label('summary_scribble')) + '</p>' +
        '<h3>' + escapeHtml(offer.name) + '</h3>' +
        '<dl>' +
        '<div><dt>' + escapeHtml(this.label('summary_guests')) + '</dt><dd>' + this.draft.partySize + '</dd></div>' +
        '<div><dt>' + escapeHtml(this.label('summary_time')) + '</dt><dd>' +
        escapeHtml(this.draft.startsAt ? this.formatDateTime(this.draft.startsAt) : this.label('summary_not_picked')) +
        '</dd></div>' +
        '<div><dt>' + escapeHtml(this.label('summary_payment')) + '</dt><dd>' +
        escapeHtml(total === 0
          ? this.label('offer_pay_in_studio')
          : fill(this.label('summary_due'), { money: this.formatMoney(total) })) +
        '</dd></div>' +
        '</dl>' +
        '<p>' + escapeHtml(offer.priceNote) + '</p>';
    }

    /* --------------------------------------------------------------- events */

    handleClick(event) {
      var chooseButton = event.target.closest('[data-choose]');
      if (chooseButton) {
        this.chooseOffer(chooseButton.getAttribute('data-choose'));
        return;
      }

      var slotButton = event.target.closest('[data-slot]');
      if (slotButton) {
        this.draft.startsAt = slotButton.getAttribute('data-slot');
        this.renderSlots();
        this.renderSummary();
        return;
      }

      var backButton = event.target.closest('[data-goto]');
      if (backButton) {
        this.step = Number(backButton.getAttribute('data-goto'));
        this.render();
        return;
      }

      var continueButton = event.target.closest('[data-continue]');
      if (continueButton) {
        if (continueButton.getAttribute('data-continue') === 'details') this.continueToDetails();
        else this.continueToReview();
      }
    }

    handleInput(event) {
      var target = event.target;
      var customerField = target.getAttribute('data-customer');
      if (customerField) {
        this.draft.customer[customerField] = target.value;
        return;
      }
      var field = target.getAttribute('data-field');
      if (field === 'notes') {
        this.draft.notes = target.value;
      }
    }

    handleChange(event) {
      var target = event.target;
      var field = target.getAttribute('data-field');

      if (field === 'childGuests') {
        this.draft.childGuests = Number(target.value);
        this.renderSummary();
        return;
      }

      if (field === 'partySize') {
        this.draft.partySize = Number(target.value);
        this.draft.startsAt = '';
        this.slots = [];
        this.availabilityState = this.draft.date ? 'loading' : 'idle';
        this.renderSlots();
        this.renderSummary();
        this.loadAvailability();
        return;
      }

      if (field === 'date') {
        this.draft.date = target.value;
        this.draft.startsAt = '';
        this.slots = [];
        this.availabilityState = target.value ? 'loading' : 'idle';
        this.renderSlots();
        this.renderSummary();
        this.loadAvailability();
        return;
      }

      var consent = target.getAttribute('data-consent');
      if (consent === 'age') this.draft.agePolicyAcknowledged = target.checked;
    }

    /* ----------------------------------------------------------- navigation */

    chooseOffer(offerId) {
      var selected = this.offers.filter(function (item) { return item.id === offerId; })[0];
      if (!selected) return;
      this.draft.offerId = selected.id;
      this.draft.partySize = selected.minimumPartySize;
      this.draft.childGuests = 0;
      this.draft.date = '';
      this.draft.startsAt = '';
      this.draft.agePolicyAcknowledged = false;
      this.slots = [];
      this.availabilityState = 'idle';
      this.step = 1;
      this.error = '';
      this.render();
    }

    continueToDetails() {
      if (!this.draft.startsAt) {
        this.setError(this.label('error_pick_time'));
        return;
      }
      this.error = '';
      this.step = 2;
      this.render();
    }

    continueToReview() {
      if (!this.form.reportValidity()) return;
      this.error = '';
      this.step = 3;
      this.render();
    }

    /* --------------------------------------------------------------- submit */

    submitBooking(event) {
      event.preventDefault();
      var offer = this.offer;
      if (!offer || !this.draft.startsAt || this.submitting) return;

      var reserve = offer.quantityMode === 'none';
      if (!reserve && !offer.variantId) {
        this.setError(this.label('error_not_bookable'));
        return;
      }

      this.submitting = true;
      this.error = '';
      this.renderStep();
      this.renderError();

      if (reserve) {
        this.reserveTable(offer);
        return;
      }

      var self = this;
      var cartUrl = (this.config.cartUrl || '/cart/add') + '.js';

      fetch(cartUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: this.cartItems(offer) })
      })
        .then(function (response) {
          return response.json().then(function (result) {
            return { ok: response.ok, result: result };
          });
        })
        .then(function (payload) {
          if (!payload.ok) {
            throw new Error(
              payload.result.description || payload.result.message || self.label('error_submit')
            );
          }
          // Shopify checkout takes it from here, and Shopify Payments collects.
          window.location.assign(self.config.checkoutUrl || '/checkout');
        })
        .catch(function (reason) {
          self.submitting = false;
          self.renderStep();
          self.setError(reason instanceof Error ? reason.message : self.label('error_submit'));
        });
    }

    /** Tables need no payment: the calendar web app saves them straight away. */
    reserveTable(offer) {
      var draft = this.draft;
      var request = {
        action: 'reserve',
        offerId: offer.id,
        startsAt: draft.startsAt,
        guests: draft.partySize,
        firstName: draft.customer.firstName,
        lastName: draft.customer.lastName,
        email: draft.customer.email,
        phone: draft.customer.phone,
        notes: draft.notes
      };
      // The same details keep the same key, so a retry cannot book the table twice.
      var signature = JSON.stringify(request);
      if (signature !== this.requestSignature) {
        this.requestSignature = signature;
        this.idempotencyKey = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      }
      request.requestId = this.idempotencyKey;

      var self = this;
      fetch(this.config.availabilityUrl, {
        method: 'POST',
        // text/plain keeps this a simple request; Apps Script cannot answer a CORS preflight.
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(request)
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (result) {
          if (!result || !result.ok) return Promise.reject((result && result.error) || '');
          var booking = {
            status: 'confirmed',
            reference: result.reference,
            offerName: offer.name,
            startsAt: result.startsAt,
            endsAt: result.endsAt,
            timezone: self.timezone,
            partySize: draft.partySize,
            totalCents: 0,
            notes: draft.notes,
            customer: { name: draft.customer.firstName + ' ' + draft.customer.lastName }
          };
          try {
            sessionStorage.setItem('potterme-booking:' + result.reference, JSON.stringify(booking));
          } catch (reason) {
            /* Without storage the ticket page shows the reference and the studio's contact. */
          }
          var path = self.config.confirmationPath || '/pages/booking-confirmation';
          window.location.assign(path + '?reference=' + encodeURIComponent(result.reference));
        })
        .catch(function (message) {
          // The web app's own messages are shown; a network failure gets the generic one.
          self.submitting = false;
          self.renderStep();
          self.setError(typeof message === 'string' && message ? message : self.label('error_submit'));
        });
    }
  }

  if (!customElements.get('potterme-booking-wizard')) {
    customElements.define('potterme-booking-wizard', PotterMeBookingWizard);
  }
})();
